import type {
  HospitalAggAvgBedDaysDayItem,
  HospitalAggAvgBedDaysMonthItem,
  HospitalAggAvgBedDaysYearItem,
  HospitalAggBedProfileDayItem,
  HospitalAggBedProfileMonthItem,
  HospitalAggBedProfileYearItem,
  HospitalAggCasesDayItem,
  HospitalAggCasesMonthItem,
  HospitalAggDepartmentItem,
  HospitalAggDoctorDayItem,
  HospitalAggDoctorItem,
  HospitalAggDoctorMonthItem,
  HospitalAggMkb10DayItem,
  HospitalAggMkb10MonthItem,
  HospitalAggMkb10YearItem,
  HospitalAggOutcomeYearItem,
  HospitalAggPlaceItem,
  HospitalAggYearFundingItem,
  HospitalBootstrapResponse,
  HospitalPlace,
} from "./types";

type Primitive = string | number | boolean | null | undefined;

type AuthPayload = {
  access_token: string;
  token_type?: string;
  iin: string;
  login?: string;
  fio?: string;
};

const HOSPITAL_FETCH_CACHE_TTL_MS = 5 * 60 * 1000;
const hospitalFetchCache = new Map<string, { ts: number; data: unknown }>();
const hospitalFetchInflight = new Map<string, Promise<unknown>>();

function getApiBase(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;

  const dashboardsBase = String(env?.VITE_DASHBOARDS_API_BASE || "").replace(/\/+$/, "");
  if (dashboardsBase) {
    return dashboardsBase;
  }

  return String(env?.VITE_API_BASE || "").replace(/\/+$/, "");
}

function buildUrl(path: string, query?: Record<string, Primitive>): string {
  const base = getApiBase();
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (/\/api$/i.test(base) && /^\/api\//i.test(normalizedPath)) {
    normalizedPath = normalizedPath.replace(/^\/api/i, "");
  }

  const url = `${base}${normalizedPath}`;
  const full = new URL(url, window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      full.searchParams.set(key, String(value));
    });
  }

  return full.toString();
}

function getStoredAuth(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthPayload;
    if (!parsed?.access_token || !parsed?.iin) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getStoredDashboardRole(): string {
  try {
    return String(localStorage.getItem("dashboards_role") || "").trim().toUpperCase();
  } catch {
    return "";
  }
}

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      if (data && typeof data === "object" && "detail" in data) {
        message = String((data as { detail?: unknown }).detail ?? message);
      }
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

async function getJson<T>(path: string, query?: Record<string, Primitive>): Promise<T> {
  const auth = getStoredAuth();
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (auth?.access_token) {
    headers.Authorization = `Bearer ${auth.access_token}`;
  }
  if (auth?.iin) {
    headers["X-User-IIN"] = auth.iin;
  }

  const response = await fetch(buildUrl(path, query), {
    method: "GET",
    credentials: "omit",
    headers,
  });

  return parseJsonOrThrow<T>(response);
}

async function getJsonCached<T>(path: string, query?: Record<string, Primitive>): Promise<T> {
  const auth = getStoredAuth();
  const role = getStoredDashboardRole();
  const cacheKey = `${buildUrl(path, query)}|${auth?.iin || ""}|${role}|${auth?.access_token || ""}`;
  const cached = hospitalFetchCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < HOSPITAL_FETCH_CACHE_TTL_MS) {
    return cached.data as T;
  }

  const inflight = hospitalFetchInflight.get(cacheKey);
  if (inflight) return (await inflight) as T;

  const fetchPromise = getJson<T>(path, query).then((data) => {
    hospitalFetchCache.set(cacheKey, { ts: Date.now(), data });
    return data;
  });

  hospitalFetchInflight.set(cacheKey, fetchPromise);
  try {
    return (await fetchPromise) as T;
  } finally {
    hospitalFetchInflight.delete(cacheKey);
  }
}

export interface HospitalCommonParams {
  mis_id?: string | null;
  place?: HospitalPlace | null;
  recent_years?: number | null;
}

export interface HospitalFinanceParams extends HospitalCommonParams {
  finance_source_id?: number | null;
}

export interface HospitalOutcomeParams extends HospitalCommonParams {
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalYearParams extends HospitalCommonParams {
  year?: number | null;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalYearMonthParams extends HospitalCommonParams {
  year: number;
  month: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalBedProfileMonthParams extends HospitalCommonParams {
  year: number;
  bed_profile_id: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalBedProfileDayParams extends HospitalCommonParams {
  year: number;
  month: number;
  bed_profile_id: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalMkb10MonthParams extends HospitalCommonParams {
  year: number;
  mkb10_id: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalMkb10DayParams extends HospitalCommonParams {
  year: number;
  month: number;
  mkb10_id: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalDepartmentParams extends HospitalCommonParams {
  year?: number | null;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalDoctorsParams extends HospitalCommonParams {
  year: number;
  department_id: number;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalDoctorMonthParams extends HospitalCommonParams {
  year: number;
  department_id: number;
  performer_employee_id: string;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export interface HospitalDoctorDayParams extends HospitalCommonParams {
  year: number;
  month: number;
  department_id: number;
  performer_employee_id: string;
  finance_source_id?: number | null;
  treatment_outcome_id?: number | null;
}

export async function fetchHospitalBootstrap(
  mis_id?: string | null,
  recent_years?: number | null,
): Promise<HospitalBootstrapResponse> {
  return getJsonCached<HospitalBootstrapResponse>("/api/v1/hospital/bootstrap", {
    mis_id: mis_id ?? undefined,
    recent_years: recent_years ?? undefined,
  });
}

export async function fetchHospitalPlace(
  mis_id?: string | null,
): Promise<HospitalAggPlaceItem[]> {
  return getJsonCached<HospitalAggPlaceItem[]>("/api/v1/hospital/agg/place", {
    mis_id: mis_id ?? undefined,
  });
}

export async function fetchHospitalYearFunding(
  params: HospitalCommonParams,
): Promise<HospitalAggYearFundingItem[]> {
  return getJsonCached<HospitalAggYearFundingItem[]>("/api/v1/hospital/agg/year-funding", {
    mis_id: params.mis_id,
    place: params.place ?? undefined,
  });
}

export async function fetchHospitalOutcomeYear(
  params: HospitalFinanceParams,
): Promise<HospitalAggOutcomeYearItem[]> {
  return getJsonCached<HospitalAggOutcomeYearItem[]>("/api/v1/hospital/agg/outcome-year", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalBedDaysYear(
  params: HospitalOutcomeParams,
): Promise<HospitalAggAvgBedDaysYearItem[]> {
  return getJsonCached<HospitalAggAvgBedDaysYearItem[]>("/api/v1/hospital/agg/bed-days-year", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalBedDaysMonth(
  params: HospitalYearParams,
): Promise<HospitalAggAvgBedDaysMonthItem[]> {
  return getJsonCached<HospitalAggAvgBedDaysMonthItem[]>("/api/v1/hospital/agg/bed-days-month", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalBedDaysDay(
  params: HospitalYearMonthParams,
): Promise<HospitalAggAvgBedDaysDayItem[]> {
  return getJsonCached<HospitalAggAvgBedDaysDayItem[]>("/api/v1/hospital/agg/bed-days-day", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    month: params.month,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalCasesMonth(
  params: HospitalYearParams,
): Promise<HospitalAggCasesMonthItem[]> {
  return getJsonCached<HospitalAggCasesMonthItem[]>("/api/v1/hospital/agg/cases-month", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    year: params.year ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalCasesDay(
  params: HospitalYearMonthParams,
): Promise<HospitalAggCasesDayItem[]> {
  return getJsonCached<HospitalAggCasesDayItem[]>("/api/v1/hospital/agg/cases-day", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    month: params.month,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalBedProfileYear(
  params: HospitalYearParams,
): Promise<HospitalAggBedProfileYearItem[]> {
  return getJsonCached<HospitalAggBedProfileYearItem[]>("/api/v1/hospital/agg/bed-profile-year", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    year: params.year ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalBedProfileMonth(
  params: HospitalBedProfileMonthParams,
): Promise<HospitalAggBedProfileMonthItem[]> {
  return getJsonCached<HospitalAggBedProfileMonthItem[]>("/api/v1/hospital/agg/bed-profile-month", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    bed_profile_id: params.bed_profile_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalBedProfileDay(
  params: HospitalBedProfileDayParams,
): Promise<HospitalAggBedProfileDayItem[]> {
  return getJsonCached<HospitalAggBedProfileDayItem[]>("/api/v1/hospital/agg/bed-profile-day", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    month: params.month,
    bed_profile_id: params.bed_profile_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalMkb10Year(
  params: HospitalYearParams,
): Promise<HospitalAggMkb10YearItem[]> {
  return getJsonCached<HospitalAggMkb10YearItem[]>("/api/v1/hospital/agg/mkb10-year", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    year: params.year ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalMkb10Month(
  params: HospitalMkb10MonthParams,
): Promise<HospitalAggMkb10MonthItem[]> {
  return getJsonCached<HospitalAggMkb10MonthItem[]>("/api/v1/hospital/agg/mkb10-month", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    mkb10_id: params.mkb10_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalMkb10Day(
  params: HospitalMkb10DayParams,
): Promise<HospitalAggMkb10DayItem[]> {
  return getJsonCached<HospitalAggMkb10DayItem[]>("/api/v1/hospital/agg/mkb10-day", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    month: params.month,
    mkb10_id: params.mkb10_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalDepartments(
  params: HospitalDepartmentParams,
): Promise<HospitalAggDepartmentItem[]> {
  return getJsonCached<HospitalAggDepartmentItem[]>("/api/v1/hospital/agg/departments", {
    mis_id: params.mis_id ?? undefined,
    place: params.place ?? undefined,
    year: params.year ?? undefined,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
    recent_years: params.recent_years ?? undefined,
  });
}

export async function fetchHospitalDoctors(
  params: HospitalDoctorsParams,
): Promise<HospitalAggDoctorItem[]> {
  return getJsonCached<HospitalAggDoctorItem[]>("/api/v1/hospital/agg/doctors", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    department_id: params.department_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalDoctorMonth(
  params: HospitalDoctorMonthParams,
): Promise<HospitalAggDoctorMonthItem[]> {
  return getJsonCached<HospitalAggDoctorMonthItem[]>("/api/v1/hospital/agg/doctor-month", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    department_id: params.department_id,
    performer_employee_id: params.performer_employee_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

export async function fetchHospitalDoctorDay(
  params: HospitalDoctorDayParams,
): Promise<HospitalAggDoctorDayItem[]> {
  return getJsonCached<HospitalAggDoctorDayItem[]>("/api/v1/hospital/agg/doctor-day", {
    mis_id: params.mis_id,
    place: params.place,
    year: params.year,
    month: params.month,
    department_id: params.department_id,
    performer_employee_id: params.performer_employee_id,
    finance_source_id: params.finance_source_id ?? undefined,
    treatment_outcome_id: params.treatment_outcome_id ?? undefined,
  });
}

// Табличные отчёты стационара (/api/v1/hospital/report/*): путь уже с query-строкой.
export function fetchHospitalReport<T>(pathWithQuery: string): Promise<T> {
  return getJsonCached<T>(pathWithQuery);
}
